import { useRolodexEntries } from 'features/rolodex'
import isNil from 'lodash/isNil'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

import { SelectedTab } from './types'

interface UseNavbarConfigProps {
  orgId: string
  siteId: string | undefined
}

const selectedTabValues = Object.values(SelectedTab)

const selectedTabLookup = new Map(
  selectedTabValues.map((tab) => [tab.toLowerCase(), tab])
)

const findSelectedTab = (item: string): SelectedTab | undefined => {
  return selectedTabLookup.get(item.toLowerCase())
}

export const useNavbarConfig = ({ orgId, siteId }: UseNavbarConfigProps) => {
  const {
    navbarConfigs: navbarConfigEntries,
    isLoading: isNavbarConfigLoading,
  } = useRolodexEntries({
    type: [RolodexEntryType.NAVBAR_CONFIG],
    tags: {
      organization: [orgId],
    },
  })

  const applicableNavbarConfig = useMemo(() => {
    const orgConfig = navbarConfigEntries.find(
      (entry) => entry.tags.organization === orgId && isNil(entry.tags.site)
    )
    const siteConfig = navbarConfigEntries.find(
      (entry) => entry.tags.organization === orgId && entry.tags.site === siteId
    )
    return siteConfig ?? orgConfig
  }, [navbarConfigEntries, orgId, siteId])

  const allowedNavbarItems = useMemo((): SelectedTab[] | undefined => {
    if (isNavbarConfigLoading) {
      return []
    }

    const allowedItems = applicableNavbarConfig?.data.allowed_navbar_items

    if (!allowedItems || allowedItems.length === 0) {
      return undefined
    }

    const filteredItems = allowedItems
      .map(findSelectedTab)
      .filter((item): item is SelectedTab => item !== undefined)

    return filteredItems.length > 0 ? filteredItems : undefined
  }, [isNavbarConfigLoading, applicableNavbarConfig])

  const showHomebarSearch = useMemo(() => {
    const showHomebarSearch = applicableNavbarConfig?.data.show_homebar_search
    return showHomebarSearch === undefined ? true : Boolean(showHomebarSearch)
  }, [applicableNavbarConfig])

  const embeddedSidebar = useMemo(() => {
    return Boolean(applicableNavbarConfig?.data.embedded_sidebar)
  }, [applicableNavbarConfig])

  return {
    allowedNavbarItems,
    isLoading: isNavbarConfigLoading,
    showHomebarSearch,
    embeddedSidebar,
  }
}
